import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import api from "../lib/api";
import { getPublicAccessInfo } from "../lib/accessPassApi";
import BrandLogo from "../components/BrandLogo";
import { Button } from "../components/ui/button";
import { useAuth } from "../context/AuthContext";
import {
  Calendar,
  MapPin,
  Users,
  Globe2,
  Gavel,
  ArrowRight,
  ChevronDown,
  Quote,
  ScanLine,
} from "lucide-react";

export default function PublicEvent() {
  const { slug } = useParams();
  const [bundle, setBundle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accessInfo, setAccessInfo] = useState(null);
  const { user } = useAuth();
  const nav = useNavigate();

  useEffect(() => {
    // Prefer the enriched microsite endpoint, fall back to the legacy one
    api
      .get(`/events/public/${slug}/full`)
      .then((r) => setBundle(r.data))
      .catch(() =>
        api
          .get(`/events/public/${slug}`)
          .then((r) => setBundle(r.data))
          .catch(() => toast.error("Event not found"))
      )
      .finally(() => setLoading(false));
    // Visitor pass CTA is opt-in per event
    getPublicAccessInfo(slug)
      .then(setAccessInfo)
      .catch(() => setAccessInfo(null));
  }, [slug]);

  const registerNow = async () => {
    if (!user) {
      nav("/login");
      return;
    }
    if (bundle?.microsite?.cta_link_override) {
      window.open(bundle.microsite.cta_link_override, "_blank");
      return;
    }
    try {
      const { data } = await api.post("/registrations", { event_id: bundle.event.id });
      await api.post(`/registrations/${data.id}/mock-pay`);
      toast.success("Registration successful! Mock payment complete.");
      nav("/dashboard/events");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not register");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-slate-500">
        Loading event…
      </div>
    );
  }
  if (!bundle) return null;

  const { event, organization, councils } = bundle;
  const microsite = bundle.microsite || {};
  const sponsors = bundle.sponsors || [];
  const themeColor = microsite.theme_color || "#0F4C81";
  const accentColor = microsite.accent_color || "#D4AF37";
  const heroBg = microsite.hero_image || event.banner_url;
  const ctaLabel = microsite.cta_label || "Register";

  return (
    <div className="min-h-screen bg-white">
      <header className="glass sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <BrandLogo />
          <div className="flex items-center gap-3">
            <Link to={user ? "/dashboard" : "/login"}>
              <Button variant="ghost" data-testid="header-signin">
                {user ? "Dashboard" : "Sign in"}
              </Button>
            </Link>
            <Button
              onClick={registerNow}
              className="font-bold text-[#0A192F]"
              style={{ backgroundColor: accentColor }}
              data-testid="header-register-button"
            >
              {ctaLabel}
            </Button>
            {accessInfo?.visitor_registration_enabled && (
              <Link to={`/events/${slug}/visitor-register`}>
                <Button
                  variant="outline"
                  className="hidden sm:inline-flex bg-white/10 border-white/30 text-white hover:bg-white/20"
                  data-testid="header-visitor-register-cta"
                >
                  <ScanLine className="h-4 w-4 mr-2" />
                  Visitor pass
                </Button>
              </Link>
            )}
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden border-b border-slate-200">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${heroBg})` }}
        />
        <div
          className="absolute inset-0"
          style={{ background: `linear-gradient(90deg, ${themeColor}f5 0%, ${themeColor}bf 50%, ${themeColor}4d 100%)` }}
        />
        <div className="relative max-w-7xl mx-auto px-6 py-20 lg:py-28 grid lg:grid-cols-12 gap-10">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            className="lg:col-span-8 text-white"
          >
            <p
              className="text-[10px] uppercase tracking-[0.28em] font-bold"
              style={{ color: accentColor }}
            >
              {microsite.hero_eyebrow || `Hosted by ${organization?.name}`}
            </p>
            <h1 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tighter leading-[1.05]">
              {event.title}
            </h1>
            <p className="mt-5 text-slate-200 max-w-2xl text-base lg:text-lg leading-relaxed">
              {microsite.hero_subtitle || event.description}
            </p>
            <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-4">
              <Meta icon={Calendar} label="Starts" value={new Date(event.start_date).toLocaleDateString()} accentColor={accentColor} />
              <Meta icon={MapPin} label="Where" value={event.location} accentColor={accentColor} />
              <Meta icon={Globe2} label="Format" value={event.format} accentColor={accentColor} />
              <Meta icon={Users} label="Cap" value={`${event.max_delegates}`} accentColor={accentColor} />
            </div>
            <div className="mt-10 flex items-center gap-4">
              <Button
                onClick={registerNow}
                className="h-12 px-6 font-bold uppercase tracking-[0.16em] text-sm text-[#0A192F]"
                style={{ backgroundColor: accentColor }}
                data-testid="hero-register-button"
              >
                {ctaLabel} — ₹{event.price?.toLocaleString()} <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
              <a href="#councils">
                <Button
                  variant="outline"
                  className="h-12 px-6 border-white/30 bg-transparent text-white hover:bg-white/10 hover:text-white"
                  data-testid="view-councils-button"
                >
                  View councils
                </Button>
              </a>
            </div>
          </motion.div>
          <div className="lg:col-span-4 hidden lg:block">
            <div className="rounded-xl bg-[#0A192F]/85 border border-[#D4AF37]/20 p-6">
              <p className="text-[10px] uppercase tracking-[0.22em] font-bold" style={{ color: accentColor }}>
                Organiser
              </p>
              <p className="mt-3 text-white font-semibold">{organization?.name}</p>
              <p className="mt-1 text-xs text-slate-400">{organization?.tagline}</p>
              <div className="mt-5 pt-5 border-t border-white/10 space-y-3">
                <FactoidRow label="Councils" value={councils?.length || 0} />
                <FactoidRow
                  label="Duration"
                  value={`${Math.max(
                    1,
                    Math.round((new Date(event.end_date) - new Date(event.start_date)) / 86400000)
                  )} days`}
                />
                <FactoidRow label="Pricing" value={`₹${event.price?.toLocaleString()}`} />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About */}
      {microsite.about_html && (
        <section className="max-w-4xl mx-auto px-6 py-16">
          <p className="text-[10px] uppercase tracking-[0.28em] font-bold" style={{ color: accentColor }}>
            About
          </p>
          <h2 className="mt-3 text-3xl sm:text-4xl font-bold tracking-tighter text-[#0A192F]">
            Why this conference
          </h2>
          <div className="mt-6 text-base text-slate-700 leading-relaxed whitespace-pre-line">
            {microsite.about_html}
          </div>
        </section>
      )}

      {/* Agenda */}
      {(microsite.agenda || []).length > 0 && (
        <section className="bg-[#F6F8FB] border-y border-slate-200">
          <div className="max-w-5xl mx-auto px-6 py-16">
            <p className="text-[10px] uppercase tracking-[0.28em] font-bold" style={{ color: accentColor }}>
              Schedule
            </p>
            <h2 className="mt-3 text-3xl sm:text-4xl font-bold tracking-tighter text-[#0A192F]">
              Agenda
            </h2>
            <div className="mt-10 space-y-4">
              {microsite.agenda.map((row, i) => (
                <div
                  key={i}
                  className="rounded-xl bg-white border border-slate-200 p-5 grid sm:grid-cols-12 gap-4"
                >
                  <div className="sm:col-span-3">
                    <p className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-bold">
                      {row.day}
                    </p>
                    <p className="mt-1 font-mono text-sm text-[#0A192F]">{row.time}</p>
                  </div>
                  <div className="sm:col-span-9">
                    <p className="font-semibold text-[#0A192F]">{row.title}</p>
                    {row.description && (
                      <p className="mt-1 text-sm text-slate-600">{row.description}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Speakers */}
      {(microsite.speakers || []).length > 0 && (
        <section className="max-w-7xl mx-auto px-6 py-16">
          <p className="text-[10px] uppercase tracking-[0.28em] font-bold" style={{ color: accentColor }}>
            On stage
          </p>
          <h2 className="mt-3 text-3xl sm:text-4xl font-bold tracking-tighter text-[#0A192F]">
            Speakers
          </h2>
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {microsite.speakers.map((s, i) => (
              <div key={i} className="rounded-xl bg-white border border-slate-200 p-6">
                {s.image ? (
                  <img src={s.image} alt={s.name} className="h-16 w-16 rounded-full object-cover" />
                ) : (
                  <div
                    className="h-16 w-16 rounded-full flex items-center justify-center text-white font-bold"
                    style={{ backgroundColor: themeColor }}
                  >
                    {(s.name || "?").slice(0, 1)}
                  </div>
                )}
                <p className="mt-4 font-semibold text-[#0A192F]">{s.name}</p>
                <p className="mt-1 text-xs uppercase tracking-[0.18em] text-slate-500">
                  {s.role}
                </p>
                {s.bio && <p className="mt-3 text-sm text-slate-600 leading-relaxed">{s.bio}</p>}
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Councils */}
      <section id="councils" className="max-w-7xl mx-auto px-6 py-16">
        <p className="text-[10px] uppercase tracking-[0.28em] font-bold" style={{ color: accentColor }}>
          Committees
        </p>
        <h2 className="mt-3 text-3xl sm:text-4xl font-bold tracking-tighter text-[#0A192F]">
          Choose your floor
        </h2>
        <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {(councils || []).map((c) => (
            <div
              key={c.id}
              className="group relative rounded-xl border border-slate-200 bg-white p-6 hover:-translate-y-1 hover:shadow-md transition-all"
            >
              <Gavel className="absolute top-5 right-5 h-5 w-5" style={{ color: `${accentColor}80` }} />
              <p className="text-[10px] uppercase tracking-[0.22em] font-bold" style={{ color: themeColor }}>
                Committee
              </p>
              <h3 className="mt-3 text-xl font-semibold tracking-tight text-[#0A192F]">{c.name}</h3>
              <p className="mt-2 text-sm font-serif italic" style={{ color: themeColor }}>
                {c.topic}
              </p>
              <p className="mt-3 text-sm text-slate-600 leading-relaxed line-clamp-3">
                {c.description}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* Sponsors */}
      {sponsors.length > 0 && (
        <section className="bg-[#F6F8FB] border-y border-slate-200">
          <div className="max-w-5xl mx-auto px-6 py-16">
            <p className="text-[10px] uppercase tracking-[0.28em] font-bold" style={{ color: accentColor }}>
              Powered by
            </p>
            <h2 className="mt-3 text-3xl sm:text-4xl font-bold tracking-tighter text-[#0A192F]">
              Our sponsors
            </h2>
            <div className="mt-10 grid sm:grid-cols-3 gap-6">
              {sponsors.map((s) => (
                <div key={s.id} className="rounded-xl bg-white border border-slate-200 p-6">
                  <p
                    className="text-[10px] uppercase tracking-[0.22em] font-bold"
                    style={{ color: accentColor }}
                  >
                    {s.tier}
                  </p>
                  <p className="mt-2 font-semibold text-[#0A192F]">{s.name}</p>
                  {s.tagline && <p className="mt-1 text-sm text-slate-500">{s.tagline}</p>}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* FAQs */}
      {(microsite.faqs || []).length > 0 && (
        <section className="max-w-3xl mx-auto px-6 py-16">
          <p className="text-[10px] uppercase tracking-[0.28em] font-bold" style={{ color: accentColor }}>
            FAQ
          </p>
          <h2 className="mt-3 text-3xl sm:text-4xl font-bold tracking-tighter text-[#0A192F]">
            Frequently asked
          </h2>
          <div className="mt-8 space-y-3">
            {microsite.faqs.map((f, i) => (
              <details
                key={i}
                className="rounded-xl border border-slate-200 bg-white p-5 group"
              >
                <summary className="flex items-center justify-between cursor-pointer text-[#0A192F] font-semibold">
                  <span>{f.q}</span>
                  <ChevronDown className="h-4 w-4 text-slate-400 transition-transform group-open:rotate-180" />
                </summary>
                <p className="mt-3 text-sm text-slate-600 leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-6 pb-20">
        <div
          className="rounded-2xl p-10 flex flex-wrap items-center justify-between gap-6"
          style={{ backgroundColor: themeColor }}
        >
          <div>
            <h3 className="text-2xl font-bold text-white tracking-tighter">
              Your seat is waiting.
            </h3>
            <p className="mt-2 text-slate-300 text-sm">
              One-click registration · QR-verified certificates · Diplomexa Conference OS.
            </p>
          </div>
          <Button
            onClick={registerNow}
            className="h-12 px-6 text-[#0A192F] font-bold uppercase tracking-[0.18em]"
            style={{ backgroundColor: accentColor }}
            data-testid="bottom-register-button"
          >
            {ctaLabel}
          </Button>
        </div>
      </section>
    </div>
  );
}

function Meta({ icon: Icon, label, value, accentColor }) {
  return (
    <div className="flex items-start gap-3">
      <div
        className="h-9 w-9 rounded-md flex items-center justify-center"
        style={{
          backgroundColor: `${accentColor}1a`,
          border: `1px solid ${accentColor}66`,
        }}
      >
        <Icon className="h-4 w-4" style={{ color: accentColor }} />
      </div>
      <div>
        <p className="text-[10px] uppercase tracking-[0.22em] text-slate-300">{label}</p>
        <p className="text-sm font-semibold text-white mt-1">{value}</p>
      </div>
    </div>
  );
}

function FactoidRow({ label, value }) {
  return (
    <div className="flex items-center justify-between text-sm">
      <span className="text-slate-400">{label}</span>
      <span className="text-white font-semibold">{value}</span>
    </div>
  );
}
